import DefaultTemplate from '@templates/DefaultTemplate/DefaultTemplate';
import {
  AttenderLogo,
  ButtonRow,
  CenterDiv,
  CustomButton,
  Description,
  Error,
  ErrorContainer,
  HomeButton,
  LottieWrapper,
  Text,
  TextBubble,
  TextMarvin
} from './ErrorPage.styled';
import { useAuthentication } from '@providers/AuthProvider';
import { useI18n } from '@i18n/useI18n';
import toTitleCase from '@utils/textTools';
import { useStore } from '@store/appStore';
import { useRouter } from 'next/router';
import ErrorMarvin from "@assets/svg/error-marvin.svg";
import AccessMarvin from "@assets/svg/no-access-marvin.svg";
import Arrow_Icon from "@assets/svg/d-arrow.svg";
import M_Arrow_Icon from "@assets/svg/m-arrow.svg";
import styled from '@emotion/styled';
import Lottie from 'react-lottie-player';
import Marvin_Json from '@lotties/Icecream.json'
import { useEffect } from 'react';
import { reportShownError } from '@utils/reportShownError';

// Not errors: the mobile app's login choice (200) and the demo's landing page (409).
const NORMAL_SCREENS = [200, 409];
// The "No internet" page (204) is only reported if it was showing at least this long.
const OFFLINE_REPORT_AFTER_MS = 15 * 1000;


interface IErrorPage {
  errorCode?: number;
  errorText?: string;
  errorDescription?: string;
}

export const ErrorPage = ({ errorCode, errorText = '', errorDescription = '' }: IErrorPage) => {
  const { user, logout } = useAuthentication();
  const router = useRouter();
  const { setTutorial, settings, setMobileLogin, safeArea } = useStore();
  const i18n = useI18n();

  // A full-page error the user is stuck on (login error, no access, teams-only...) - see reportShownError.
  useEffect(() => {
    if (errorCode == null || NORMAL_SCREENS.includes(errorCode)) return;
    const shownAt = Date.now();
    // The text as the user saw it (their language), plus the i18n keys - stable across languages.
    const report = (extra = {}) =>
      reportShownError('error-page', {
        code: errorCode,
        title: errorText ? i18n.t(errorText) : '',
        message: errorDescription ? i18n.t(errorDescription) : '',
        titleKey: errorText,
        messageKey: errorDescription,
        language: i18n.language,
        path: router.pathname,
        ...extra,
      });
    if (errorCode !== 204) {
      report();
      return;
    }
    // "No internet": shown while the device reports itself offline, gone again when it's back - phones
    // switching Wi-Fi/mobile data or locking for a moment. Reported when it goes away (Bugsnag can't
    // send while offline anyway), and only if it lasted.
    return () => {
      const offlineMs = Date.now() - shownAt;
      if (offlineMs >= OFFLINE_REPORT_AFTER_MS) report({ offlineSeconds: Math.round(offlineMs / 1000) });
    };
  }, [errorCode]);

  const ErrorMarvin = {
    loop: false,
    animationData: Marvin_Json,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };


  let error = <></>
  if (errorCode === 200) {
    error = <>
      <AttenderLogo mobile={errorCode === 200} />
      <ButtonRow mobile={true}>
        <CustomButton mobile={true} small={false} onClick={() => setMobileLogin(3)}>{i18n.t('Login')}</CustomButton>
        <CustomButton mobile={true} small={false} onClick={() => setMobileLogin(2)}>{`${i18n.t('go-to')} DEMO`}</CustomButton>
      </ButtonRow></>
  }
  else if (errorCode === 204) {
    error = <>
      <CenterDiv>
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t('error-nointernet-text')}</Text>
      <Description>{i18n.t('error-nointernet-title')}</Description>
    </>
  }
  else if (errorCode === 401) {
    error = <>
      <CenterDiv>
        <TextBubble>
          <TextMarvin red={false}>{i18n.t('not-authorized-page-title')}</TextMarvin>
          <Arrow_Icon />
          <M_Arrow_Icon />
        </TextBubble>
        <AccessMarvin />
      </CenterDiv>
      <ButtonRow>
        <CustomButton href="/" small={true}>{i18n.t('home')}</CustomButton>
        <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />
      </ButtonRow>
    </>
  }
  else if (errorCode === 402) {
    error = <>
      <CenterDiv>
        <TextBubble>
          <TextMarvin red={false}>{i18n.t('not-authorized-page-title')}</TextMarvin>
          <Arrow_Icon />
          <M_Arrow_Icon />
        </TextBubble>
        <AccessMarvin />
      </CenterDiv>
      <ButtonRow>

      </ButtonRow>
    </>
  }
  else if (errorCode === 403) {
    error = <>
      <CenterDiv>
        <TextBubble>
          <TextMarvin red={false}>{i18n.t('not-authorized-title')}</TextMarvin>
          <Arrow_Icon />
          <M_Arrow_Icon />
        </TextBubble>
        <AccessMarvin />
      </CenterDiv>
      <Description>{i18n.t('not-authorized-message')}</Description>
      <ButtonRow>
        {(settings?.TEAMS && settings.TEAMS === "true") ? "" : <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />}
      </ButtonRow>
    </>
  }
  else if (errorCode === 405) {
    error = <>
      <CenterDiv>
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t("login-error-title")}</Text>
      <Description>{i18n.t("login-error-message")}</Description>
      <ButtonRow>
        {(settings?.TEAMS && settings.TEAMS === "true") ? "" : <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />}
      </ButtonRow>
    </>
  }
  else if (errorCode === 503) {
    // Login couldn't load what it needs - the connection dropped (loadLoginData gave up) or Graph's group
    // lookup failed. Usually gone within seconds, so the obvious way out is to try again; phones in
    // particular have no reload button in the app.
    error = <>
      <CenterDiv>
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t(errorText || "login-error-title")}</Text>
      <Description>{i18n.t(errorDescription || "login-error-message")}</Description>
      <ButtonRow>
        <CustomButton small={true} onClick={() => window.location.reload()}>{i18n.t('retry')}</CustomButton>
        {(settings?.TEAMS && settings.TEAMS === "true") ? "" : <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />}
      </ButtonRow>
    </>
  }
  else if (errorCode === 409) {
    error = <>
      <CenterDiv>
        <TextBubble>
          <TextMarvin red={false}>{i18n.t('demo-text')}</TextMarvin>
          <Arrow_Icon />
          <M_Arrow_Icon />
        </TextBubble>
        <AccessMarvin />
      </CenterDiv>
      <Description>{i18n.t('demo-description')}</Description>
      <ButtonRow>
        <CustomButton href="/" small={true}>{i18n.t('demo-home')}</CustomButton>
        <CustomButton href="https://attender.com/" small={true} target={"_blank"}> {`${i18n.t('go-to')} attender.com`} </CustomButton>
        {settings?.TEAMS && settings.TEAMS === "true" ? "" : <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />}
      </ButtonRow>
    </>
  }
  else if (errorCode === 500 || errorCode === 404) {
    error = <>
      <CenterDiv>
        {/* <ErrorMarvin /> */}
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t("four-oh-four-message")}</Text>
      <ButtonRow>
        <CustomButton href="/" small={true}>{i18n.t('home')}</CustomButton>
      </ButtonRow>
    </>
  }
  else if (errorCode === 411) {
    error = <>
      <CenterDiv>
        {/* <ErrorMarvin /> */}
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t("no-mobile")}</Text>
    </>
  }
  else if (errorCode === 412) {
    error = <>
      <CenterDiv>
        <TextBubble>
          <TextMarvin red={false}>{i18n.t('no-mobile-access')}</TextMarvin>
          <Arrow_Icon />
          <M_Arrow_Icon />
        </TextBubble>
        <AccessMarvin />
      </CenterDiv>
      <Description>{i18n.t('no-mobile-access-description')}</Description>
    </>
  }
  else if (errorCode === 413) {
    // The Teams build (teams.attender.dk, /teamExtension links) opened in an ordinary browser - it can
    // only sign in through Teams.
    // Same layout as 411 (figure, then text) - the speech-bubble layout overlaps its figure on phones.
    error = <>
      <CenterDiv>
        <LottieWrapper>
          <Lottie {...ErrorMarvin} play style={{width:'100%',height:'100%',overflow:'hidden',margin:'0 auto'}} />
        </LottieWrapper>
      </CenterDiv>
      <Text>{i18n.t('teams-only-title')}</Text>
      <Description>{i18n.t('teams-only-description')}</Description>
      <ButtonRow>
        <CustomButton href="https://login.attender.com/" small={true}>{i18n.t('teams-only-open-web')}</CustomButton>
      </ButtonRow>
    </>
  }
  else {
    error = <>
      <Text>{i18n.t(errorDescription)}</Text>
      <Description>{i18n.t(errorText)}</Description>
      {user && <Description>logged in as {user.username}</Description>}
      <ButtonRow>

        <CustomButton href="https://attender.com/" small={true} target={"_blank"}> {`${i18n.t('go-to')} attender.com`} </CustomButton>
        <HomeButton title={i18n.t('menu-logout')} onClick={logout} small={true} />
      </ButtonRow>
    </>
  }


  return (
    <DefaultTemplate>
      <ErrorContainer>
        {error}
      </ErrorContainer>
    </DefaultTemplate>
  );
};

export default ErrorPage;
